/**
 * ActivateVersionControl.tsx — the "activate/rollback a version" review +
 * confirm flow (story E0-BO-CFG-ACTIVATE-EXISTING-VERSION-AND-AGENT-CONFIG-
 * EDITOR). Shared by the version list (activate ANY listed non-active
 * version — a rollback) and SkillVersionEditForm (activate a just-created
 * version) — the SAME control, not two copies of the same dialog. A `<dialog>`
 * review step states plainly what activation does before the operator
 * confirms (new conversations pick it up immediately; conversations already
 * in progress keep their original prompt permanently), same as the original
 * SkillVersionEditForm-only flow this replaces.
 *
 * Renders nothing for an already-active version — there is nothing to do.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UnauthorizedError, type ApiClient } from '../../api/client';
import { Button } from '../../components/Button';
import floating from '../../lib/floating';

export interface ActivateVersionControlProps {
  apiClient: ApiClient;
  skillName: string;
  configVersion: string;
  productCode: string;
  isActive: boolean;
  /** Called after a successful activation, so the caller can refresh its list/detail. */
  onActivated?: () => void;
  /**
   * Appended to every data-testid so multiple instances (one per list row)
   * never collide. Empty by default — the sole SkillVersionEditForm instance
   * keeps its original bare testids (`config-activate-review`, etc.).
   */
  testIdSuffix?: string;
}

type ActivatePhase = 'idle' | 'reviewing' | 'submitting' | 'done' | 'error';

export const ActivateVersionControl = ({
  apiClient,
  skillName,
  configVersion,
  productCode,
  isActive,
  onActivated = undefined,
  testIdSuffix = '',
}: ActivateVersionControlProps) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<ActivatePhase>('idle');
  const [error, setError] = useState<'unavailable' | 'unauthorized' | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const shouldBeOpen = phase === 'reviewing' || phase === 'submitting' || phase === 'error';
    if (shouldBeOpen && dialogRef.current != null && !dialogRef.current.open) {
      dialogRef.current.showModal();
    }
  }, [phase]);

  if (isActive) return null;

  const handleActivate = async () => {
    setPhase('submitting');
    setError(null);
    try {
      await apiClient.activateSkillVersion(skillName, configVersion, { product_code: productCode });
      setPhase('done');
      onActivated?.();
    } catch (err) {
      setPhase('reviewing');
      setError(err instanceof UnauthorizedError ? 'unauthorized' : 'unavailable');
    }
  };

  return (
    <>
      {phase === 'idle' || phase === 'done' ? (
        <Button
          data-testid={`config-activate-review${testIdSuffix}`}
          onClick={() => setPhase('reviewing')}
          className="min-h-11 w-fit"
        >
          {t('config.edit.activate')}
        </Button>
      ) : null}
      {phase === 'done' ? (
        <p
          role="status"
          data-testid={`config-activate-done${testIdSuffix}`}
          className="text-body text-status-ok"
        >
          {t('config.edit.activated', { version: configVersion })}
        </p>
      ) : null}

      {phase === 'reviewing' || phase === 'submitting' || phase === 'error' ? (
        <dialog
          ref={dialogRef}
          data-testid="config-activate-confirm"
          aria-labelledby="config-activate-confirm-heading"
          // Fires on native Esc-dismiss — the Cancel button sets phase
          // directly (unmounting this dialog via the conditional render), so
          // this only ever fires from Esc.
          onClose={() => setPhase('idle')}
          className="w-full max-w-lg rounded-lg bg-surface-1 p-4 text-ink-1 ring-1 ring-inset ring-gold/40 backdrop:bg-canvas/70"
        >
          <div className="flex flex-col gap-3">
            <h4 id="config-activate-confirm-heading" className="text-body font-medium text-ink-1">
              {t('config.edit.activateConfirmHeading')}
            </h4>
            <p className="text-body text-ink-1">{t('config.edit.activateExplain')}</p>
            {error != null ? (
              <p
                role="alert"
                data-testid={`config-activate-error-${error}`}
                className="text-meta text-status-down"
              >
                {t(`config.edit.errors.${error}`)}
              </p>
            ) : null}
            <div className="flex gap-3">
              <Button
                variant="primary"
                data-testid="config-activate-confirm-submit"
                disabled={phase === 'submitting'}
                aria-busy={phase === 'submitting'}
                onClick={() => floating(handleActivate())}
                className="min-h-11"
              >
                {phase === 'submitting'
                  ? t('config.edit.activating')
                  : t('config.edit.activateConfirm')}
              </Button>
              <Button
                data-testid="config-activate-cancel"
                disabled={phase === 'submitting'}
                onClick={() => setPhase('idle')}
                className="min-h-11"
              >
                {t('config.edit.activateCancel')}
              </Button>
            </div>
          </div>
        </dialog>
      ) : null}
    </>
  );
};
